import { ref, watch } from 'vue'
import { useProjectStore } from '@/store/project'
import { useUnitStore } from '@/store/unit'

import { hexToRgb } from '@/utils/colorHelpers'
import type Unit from '@/types/unit'

export function useApartmentChooser() {
    const status = ref('')
    const currentUnitId = ref(null)
    const currentUnit = ref<Unit | null>(null)
    const hoverUnitId = ref(null)
    const hoverUnit = ref<Unit | null>(null)

    const selectedUnitIndex = ref<number | null>(-1)

    const projectStore = useProjectStore()
    const unitStore = useUnitStore()

    const units = unitStore.units

    // TODO: use filtered units from store
    const filteredUnits = units

    const colorCallback = (unitId: string, state) => {
        let color = null
        const unit = units.find((u) => u.unitId === unitId)

        if (unit) {
            const unitStatus = projectStore.getUnitStatus(unit)

            if (!unitStatus) return color

            let colorOverrides = {}

            const status = {
                ...unitStatus,
                ...colorOverrides
            }

            if (status) {
                if (state === 'idle') {
                    if (status.persistentUnitColor) {
                        color = {
                            ...hexToRgb(status.idleColor),
                            s: status.idleStrokeWidth
                        }
                    }
                } else if (state === 'hover') {
                    color = {
                        ...hexToRgb(status.highlightColor),
                        s: status.highlightStrokeWidth
                    }
                } else if (state === 'selected') {
                    color = {
                        ...hexToRgb(status.activeColor),
                        s: status.activeStrokeWidth
                    }
                }

                if (filteredUnits.findIndex((u) => u.unitId === unit.unitId) < 0 && color) {
                    color = {
                        ...hexToRgb(status.inactiveColor),
                        s: status.inactiveStrokeWidth
                    }
                }
            }
        }
        return color
    }

    const hoverOver = (unitId: string) => {
        status.value = 'hoverOver ' + unitId

        const unit = units.find((u) => u.unitId === unitId)
        if (unit) hoverUnitId.value = unit.unitId
    }

    const hoverOut = (unitId: string) => {
        status.value = 'hoverOut ' + unitId

        const unit = units.find((u) => u.unitId === unitId)
        if (unit) hoverUnitId.value = unit.unitId
    }

    const select = (unitId: string) => {
        const unit = units.find((u) => u.unitId === unitId)
        if (unit) {
            currentUnitId.value = unit.unitId
        }
    }

    const goTo = (unitId: string, projectSlug: string, buildingSlug: string) => {
        status.value = 'goTo ' + unitId
        currentUnitId.value = unitId

        navigateWithHash(`/${projectSlug}/${buildingSlug}/${unitId}/`)
    }

    const nav = (unitId: string) => {
        // TODO: nav override from embed
        status.value = 'nav ' + unitId
        currentUnitId.value = unitId
    }

    const renderDisplay = (str: string = '', unitId: string) => {
        const unit = units.find((u) => u.unitId === unitId)
        if (unit) {
            return `${str} ${unit.title}`
        }

        return ''
    }

    const cdnFileResolver = (
        projectAssetRoot: string,
        buildingAssetPath: string,
        filename: string
    ) => {
        return `${projectAssetRoot}/buildings/${buildingAssetPath}${filename}`
    }

    watch(currentUnitId, async (newUnitId, oldUnitId) => {
        const unit = units.find((unit) => unit.unitId === newUnitId)
        if (unit) currentUnit.value = unit
    })

    watch(hoverUnitId, async (newUnitId, oldUnitId) => {
        const unit = units.find((unit) => unit.unitId === newUnitId)
        if (unit) hoverUnit.value = unit
    })

    const setCurrentUnitId = (unitId) => {
        currentUnitId.value = unitId
    }

    const setCurrentUnit = (unit) => {
        currentUnitId.value = unit.unitId
    }

    const nextUnit = computed(() => {
        if (currentUnit.value) {
            const index = filteredUnits.findIndex((u) => u.unitId === currentUnit.value.unitId)
            return index < filteredUnits.length - 1 ? filteredUnits[index + 1] : null
        } else {
            return null
        }
    })

    const prevUnit = computed(() => {
        if (currentUnit.value) {
            const index = filteredUnits.findIndex((u) => u.unitId === currentUnit.value.unitId)
            return index > 0 ? filteredUnits[index - 1] : null
        } else {
            return null
        }
    })

    const startUnit = computed(() => {
        return filteredUnits.find((u) => !projectStore.getUnitStatus(u).isSold) || filteredUnits[0]
    })

    const canGoToUnit = (unitId: string) => {
        const unit = units.find((unit) => unit.unitId === unitId)
        const unitStatus = projectStore.getUnitStatus(unit)

        if (unit && unitStatus) {
            return !unitStatus.hidePage
        }
        return false
    }

    const showCards = computed(() => {
        // TODO: get logic for embed
        return true
    })

    const build = computed(() => {
        // TODO:
        // return this.$config.build.toString()
        //old nuxt config has Date.now()??
        return Date.now().toString()
    })

    const onApartmentChooserInitialized = (paramsFromPlayerLibrary) => {
        const { scene, unitsSceneSort, initialize, scenesByApartment } = paramsFromPlayerLibrary

        let startSceneUnit
        if (selectedUnitIndex.value === -1) {
            const filteredUnitsCopy = [...filteredUnits]

            unitsSceneSort(filteredUnitsCopy)

            const startUnit =
                filteredUnitsCopy.find((u) => !projectStore.getUnitStatus(u).isSold) ||
                filteredUnitsCopy[0]

            if (startUnit) {
                currentUnitId.value = startUnit.unitId
                currentUnit.value = startUnit

                startSceneUnit = startUnit
            } else {
                startSceneUnit = units[0]
            }
        } else {
            startSceneUnit = units[selectedUnitIndex.value]
        }

        let startScene = scene

        if (startSceneUnit && startSceneUnit.unitId) {
            if (startSceneUnit.unitId in scenesByApartment) {
                startScene = scenesByApartment[startSceneUnit.unitId]
            } else if (startSceneUnit.scene) {
                startScene = startSceneUnit.scene
            }
        }

        initialize(startScene)
    }

    return {
        status,
        cdnFileResolver,
        colorCallback,
        hoverOver,
        hoverOut,
        select,
        goTo,
        nav,
        renderDisplay,
        nextUnit,
        prevUnit,
        canGoToUnit,
        startUnit,
        onApartmentChooserInitialized,
        showCards,
        build,
        currentUnitId,
        currentUnit,
        setCurrentUnitId,
        setCurrentUnit,
        hoverUnitId,
        hoverUnit
    }
}
